import { NavLink, Link, useNavigate } from 'react-router-dom';
import {
  Home, Search, Bell, Bookmark, Settings, Users, PenLine, LogOut, Globe, Radio, CircleUserRound, Newspaper,
} from 'lucide-react';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useLoginActions } from '@/hooks/useLoginActions';
import { useFeed } from '@/contexts/FeedContext';
import { useSocial } from '@/contexts/SocialContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { UserAvatar, pubkeyToNpub } from '@/components/user';
import { getDisplayName } from '@/lib/getDisplayName';
import { useAuthor } from '@/hooks/useAuthor';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { Menu, Plus, ChevronDown } from 'lucide-react';

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2 px-2 py-4">
      <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-violet-600 via-fuchsia-500 to-orange-400 text-lg font-black text-white shadow-md">
        J
      </span>
      <span className="text-xl font-black tracking-tight">
        jumble<span className="text-fuchsia-500">.</span>
      </span>
    </Link>
  );
}

const NAV = [
  { to: '/', label: 'Home', icon: Home, end: true },
  { to: '/following', label: 'Following', icon: Users },
  { to: '/articles', label: 'Articles', icon: Newspaper },
  { to: '/search', label: 'Search', icon: Search },
  { to: '/notifications', label: 'Notifications', icon: Bell },
  { to: '/bookmarks', label: 'Bookmarks', icon: Bookmark },
];

function FeedSwitcher() {
  const { relaySets, feed, setFeed } = useFeed();
  const { user } = useCurrentUser();
  const label =
    feed.feedType === 'following' ? 'Following' :
    feed.feedType === 'relay' ? (feed.name ?? 'Relay') :
    (feed.name ?? 'Feed');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-full justify-between rounded-xl">
          <span className="flex items-center gap-2 truncate">
            {feed.feedType === 'following' ? <Users className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
            <span className="truncate">{label}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align="start">
        {user && (
          <DropdownMenuItem onClick={() => setFeed({ feedType: 'following', name: 'Following' })}>
            <Users className="h-4 w-4" /> Following
            {feed.feedType === 'following' && <span className="ml-auto text-primary">●</span>}
          </DropdownMenuItem>
        )}
        {relaySets.map((s) => (
          <DropdownMenuItem key={s.id} onClick={() => setFeed({ feedType: 'relays', id: s.id, name: s.name })}>
            <Globe className="h-4 w-4" /> <span className="truncate">{s.name}</span>
            <span className="ml-auto text-xs text-muted-foreground">{s.relayUrls.length}</span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem onClick={() => setFeed({ feedType: 'relay', id: 'wss://relay.damus.io/', name: 'Damus' })}>
          <Radio className="h-4 w-4" /> Browse a relay…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AccountBlock() {
  const { user } = useCurrentUser();
  const author = useAuthor(user?.pubkey);
  const { logout } = useLoginActions();
  const navigate = useNavigate();
  if (!user) return <LoginArea className="w-full max-w-none" />;
  const name = getDisplayName(author.data?.metadata, user.pubkey);
  return (
    <div className="rounded-xl border p-3">
      <button className="flex w-full items-center gap-2 text-left" onClick={() => navigate(`/${pubkeyToNpub(user.pubkey)}`)}>
        <UserAvatar pubkey={user.pubkey} size="sm" />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">{name}</span>
      </button>
      <Button variant="ghost" size="sm" className="mt-1 w-full justify-start text-muted-foreground" onClick={() => logout()}>
        <LogOut className="h-4 w-4" /> Sign out
      </Button>
    </div>
  );
}

function SidebarBody({ onNavigate }: { onNavigate?: () => void }) {
  const { bookmarks } = useSocial();
  void bookmarks;
  return (
    <div className="flex h-full flex-col gap-1 p-3">
      <Logo />
      <FeedSwitcher />
      <nav className="mt-2 flex flex-col gap-0.5" aria-label="Primary">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium transition-colors hover:bg-accent',
                isActive ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground',
              )
            }
          >
            <Icon className="h-5 w-5" /> {label}
          </NavLink>
        ))}
        <NavLink
          to="/relays"
          onClick={onNavigate}
          className={({ isActive }) =>
            cn('flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium transition-colors hover:bg-accent',
              isActive ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground')
          }
        >
          <Radio className="h-5 w-5" /> Relays
        </NavLink>
        <NavLink
          to="/settings"
          onClick={onNavigate}
          className={({ isActive }) =>
            cn('flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium transition-colors hover:bg-accent',
              isActive ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground')
          }
        >
          <Settings className="h-5 w-5" /> Settings
        </NavLink>
      </nav>
      <Link to="/compose" onClick={onNavigate} className="mt-3">
        <Button className="w-full rounded-full py-5 text-base font-bold shadow-lg shadow-fuchsia-500/20">
          <PenLine className="h-5 w-5" /> Post
        </Button>
      </Link>
      <div className="mt-auto flex flex-col gap-3 pt-4">
        <Separator />
        <AccountBlock />
        <p className="px-2 text-[11px] leading-relaxed text-muted-foreground">
          A user-friendly Nostr client for exploring relay feeds. Vibed with{' '}
          <a href="https://shakespeare.diy" target="_blank" rel="noreferrer" className="underline">Shakespeare</a>.
        </p>
      </div>
    </div>
  );
}

export function AppShell({ children, title, right }: { children: React.ReactNode; title?: string; right?: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-6xl">
      <aside className="sticky top-0 hidden h-screen w-[270px] shrink-0 md:block">
        <SidebarBody />
      </aside>
      <main className="min-w-0 flex-1 border-x">
        <header className="sticky top-0 z-30 flex items-center gap-2 border-b bg-background/85 px-3 py-2.5 backdrop-blur-md">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 p-0">
              <ScrollArea className="h-full">
                <SidebarBody onNavigate={() => setOpen(false)} />
              </ScrollArea>
            </SheetContent>
          </Sheet>
          <h1 className="min-w-0 flex-1 truncate text-lg font-extrabold tracking-tight">{title ?? 'Home'}</h1>
          {right}
          <Link to="/compose" className="md:hidden">
            <Button size="icon" className="rounded-full" aria-label="New post">
              <Plus className="h-5 w-5" />
            </Button>
          </Link>
        </header>
        {children}
      </main>
      <aside className="sticky top-0 hidden h-screen w-80 shrink-0 overflow-y-auto p-4 lg:block">
        <RightRail />
      </aside>
      <nav className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-around border-t bg-background/95 py-1 backdrop-blur-md md:hidden" aria-label="Mobile">
        <MobileTab to="/" icon={<Home className="h-5 w-5" />} label="Home" end />
        <MobileTab to="/search" icon={<Search className="h-5 w-5" />} label="Search" />
        <MobileTab to="/notifications" icon={<Bell className="h-5 w-5" />} label="Alerts" />
        <MobileTab to="/bookmarks" icon={<Bookmark className="h-5 w-5" />} label="Saved" />
        <MobileTab to="/me" icon={<CircleUserRound className="h-5 w-5" />} label="Me" />
      </nav>
    </div>
  );
}

function MobileTab({ to, icon, label, end }: { to: string; icon: React.ReactNode; label: string; end?: boolean }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => cn('flex flex-col items-center gap-0.5 rounded-lg px-4 py-1.5 text-[10px] font-medium', isActive ? 'text-primary' : 'text-muted-foreground')}>
      {icon}{label}
    </NavLink>
  );
}

function RightRail() {
  const { relaySets } = useFeed();
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-2xl border bg-card p-4">
        <h2 className="text-base font-extrabold">Explore relay feeds</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Jumble-style browsing: pick a relay set and scroll what each relay community is saying — no account needed.
        </p>
        <Link to="/relays"><Button variant="outline" size="sm" className="mt-3 w-full">Browse relays</Button></Link>
      </div>
      <div className="rounded-2xl border bg-card p-4">
        <h2 className="text-base font-extrabold">Relay sets</h2>
        <ul className="mt-2 flex flex-col gap-1">
          {relaySets.slice(0, 6).map((s) => (
            <li key={s.id}>
              <Link to={`/feed/${s.id}`} className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm hover:bg-accent">
                <span className="truncate font-medium">{s.name}</span>
                <span className="text-xs text-muted-foreground">{s.relayUrls.length} relays</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-2xl border bg-gradient-to-br from-violet-600 via-fuchsia-500 to-orange-400 p-4 text-white">
        <h2 className="text-base font-extrabold">New to Nostr?</h2>
        <p className="mt-1 text-sm text-white/85">Your identity is a keypair. Join with an extension, private key, or bunker — then post everywhere.</p>
      </div>
    </div>
  );
}
